/**
 * career-profile — the Kompass profile view (route `/compass/profile`).
 *
 * Renders the presented German Kandidatenprofil (`profile.NarrativeAuthored`: body +
 * provenance chip + honest `Stand: v<n>` staleness cue) plus the typed facets
 * (identity, motivations, drives, values, anti-values, deal-breakers, trajectory,
 * constraints, voice) with per-facet source attribution (claimRefs → the ledger).
 * Re-author uses the EXISTING narrative procedure. Facets are VIEW-ONLY in this slice
 * (no user-owned revision procedures exist yet — named deferral in the evidence log).
 */

import {
  KitElement,
  signal,
  type ActionMap,
  type KitContext,
  type ReadonlySignal,
  type Signal,
  type ViewModel,
} from "@platform/web-kit"
import { html, nothing, classMap, type TemplateResult } from "@platform/web-kit/render"
import { I18nToken, RouterToken, SessionToken, apiGet, apiPost } from "../services"
import type { CareerI18n, CareerKey } from "../../i18n"
import type { Router, Urn } from "@platform/web-kit"
import type { ShellSession } from "../domain"
import { authoredOnDate, narrativeBlocks } from "../lib/narrative"

interface Facet {
  statement: string
  claimCount: number
}
interface ProfileView {
  throughline: string
  selfDescription: string
  motivations: Facet[]
  drives: Facet[]
  values: Facet[]
  antiValues: Facet[]
  dealBreakers: Facet[]
  trajectory: { now: string; midTerm: string; longTerm: string }
  constraints: { location: string; salaryTarget: string; workModes: string[] }
  hasVoice: boolean
  voiceSamples: string[]
}

const WORK_MODES = ["remote", "hybrid", "onsite"] as const
interface NarrativeView {
  body: string
  provenanceCount: number
  stale: boolean
  authoredOn: string | null
  /** M1: true when the persisted narrative is the deterministic composer fallback, not real prose. */
  degraded: boolean
}

const facetsOf = (raw: any[]): Facet[] =>
  (Array.isArray(raw) ? raw : []).map((f) => ({ statement: String(f?.statement ?? ""), claimCount: Array.isArray(f?.claimRefs) ? f.claimRefs.length : 0 }))

export class ProfileVm implements ViewModel {
  readonly profile: Signal<ProfileView | null> = signal<ProfileView | null>(null)
  readonly narrative: Signal<NarrativeView | null> = signal<NarrativeView | null>(null)
  readonly authoring: Signal<boolean> = signal(false)
  readonly error: Signal<string | null> = signal<string | null>(null)
  readonly loaded: Signal<boolean> = signal(false)
  /** Which facet section is being edited: "identity" | "constraints" | "voice" | null. */
  readonly editing: Signal<string | null> = signal<string | null>(null)
  readonly saving: Signal<boolean> = signal(false)
  readonly renderDeps: ReadonlyArray<ReadonlySignal<unknown>>
  readonly t: CareerI18n["t"]
  /** The per-user profile URN (AX-4: the profile entity is DOM-addressable). */
  readonly urn: Urn

  constructor(private readonly router: Router, i18n: CareerI18n, session: ShellSession) {
    this.t = i18n.t
    this.urn = `urn:career:profile:${session.user?.sub ?? "anon"}`
    this.renderDeps = [this.profile, this.narrative, this.authoring, this.error, this.loaded, this.editing, this.saving]
  }

  edit(section: string): void {
    this.editing.set(this.editing.value === section ? null : section)
  }
  cancelEdit(): void {
    this.editing.set(null)
  }

  /** Optimistic-ish: POST the revision, then refresh from server truth (absolute end-state). */
  private async saveRevision(path: string, body: unknown): Promise<void> {
    this.saving.set(true)
    this.error.set(null)
    try {
      await apiPost(path, body)
      this.editing.set(null)
      await this.refresh()
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.saving.set(false)
    }
  }

  saveIdentity(throughline: string, selfDescription: string): Promise<void> {
    return this.saveRevision("/api/profile/identity", {
      identity: { throughline: throughline.trim() || null, selfDescription: selfDescription.trim() || null },
    })
  }
  saveConstraints(location: string, salaryTarget: string, workModes: string[]): Promise<void> {
    return this.saveRevision("/api/profile/constraints", {
      constraints: { location: location.trim() || null, salaryTarget: salaryTarget.trim() || null, workModes },
    })
  }
  saveVoice(samples: string[]): Promise<void> {
    return this.saveRevision("/api/profile/voice", { voiceProfile: { samples } })
  }

  attach(): () => void {
    void this.refresh()
    return () => {}
  }

  private async refresh(): Promise<void> {
    try {
      const p = await apiGet<{ profile: any; narrative: any; narrativeStale: boolean; narrativeAuthoredAt: string | null; narrativeDegraded?: boolean }>("/api/profile")
      this.profile.set(p.profile ? toProfileView(p.profile) : null)
      this.narrative.set(p.narrative ? toNarrativeView(p.narrative, p.narrativeStale, p.narrativeAuthoredAt, Boolean(p.narrativeDegraded)) : null)
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.loaded.set(true)
    }
  }

  async authorNarrative(): Promise<void> {
    this.authoring.set(true)
    this.error.set(null)
    try {
      // Read the POST's degraded flag so the cue shows IMMEDIATELY (not only after reload).
      const res = await apiPost<{ degraded?: boolean }>("/api/profile/narrative")
      await this.refresh()
      const n = this.narrative.value
      if (n && typeof res.degraded === "boolean") this.narrative.set({ ...n, degraded: res.degraded })
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.authoring.set(false)
    }
  }

  go(url: string): void {
    void this.router.navigate(url)
  }
}

export class CareerProfile extends KitElement<ProfileVm> {
  protected createViewModel(ctx: KitContext): ProfileVm {
    return new ProfileVm(ctx.inject(RouterToken), ctx.inject(I18nToken), ctx.inject(SessionToken))
  }

  private facetList(vm: ProfileVm, titleKey: CareerKey, facets: Facet[]): TemplateResult | typeof nothing {
    if (facets.length === 0) return nothing
    return html`
      <section class="profile-facets" aria-label=${vm.t(titleKey)}>
        <h3 class="profile-facets-title" lang="de">${vm.t(titleKey)}</h3>
        <ul class="facet-list" role="list">
          ${facets.map(
            (f) => html`<li class="facet">
              <p class="facet-statement" lang="de">${f.statement}</p>
              ${f.claimCount > 0
                ? html`<a class="facet-sources chip" href="/compass/evidence" data-act="nav" data-url="/compass/evidence" lang="de">${vm.t("profile.facet.sources", { n: f.claimCount })}</a>`
                : nothing}
            </li>`,
          )}
        </ul>
      </section>
    `
  }

  private narrativePanel(vm: ProfileVm): TemplateResult {
    const n = vm.narrative.value
    return html`
      <section class=${classMap({ "profile-narrative": true, "surface-panel": true, "is-degraded": Boolean(n?.degraded) })} aria-labelledby="profile-narrative-title" aria-live="polite">
        <h2 id="profile-narrative-title" class="surface-section-title" lang="de">${vm.t("profile.narrative.title")}</h2>
        ${n
          ? html`
              ${n.degraded
                ? html`<p class="narrative-degraded-cue" role="status" lang="de">${vm.t("profile.narrative.degraded")}</p>`
                : nothing}
              <div class="narrative-body">
                ${narrativeBlocks(n.body).map((b) => (b.heading ? html`<h3 lang="de">${b.text}</h3>` : html`<p lang="de">${b.text}</p>`))}
              </div>
              <p class="narrative-meta">
                <span class="narrative-prov chip" lang="de">${vm.t("review.narrative.provenance", { n: n.provenanceCount })}</span>
                ${n.stale
                  ? html`<span class="narrative-cue chip is-stale" lang="de">${vm.t("review.narrative.stale")}</span>`
                  : n.authoredOn
                    ? html`<span class="narrative-cue chip" lang="de">${vm.t("review.narrative.authoredOn", { date: n.authoredOn })}</span>`
                    : nothing}
              </p>
              <button data-act="author-narrative" class=${classMap({ "is-pending": vm.authoring.value, "is-x-prominent": Boolean(n.degraded) })} ?disabled=${vm.authoring.value} lang="de">
                ${vm.authoring.value ? vm.t("review.narrative.authoring") : vm.t("review.narrative.reauthor")}
              </button>
            `
          : html`
              <p class="surface-empty" lang="de">${vm.t("review.narrative.empty")}</p>
              <button data-act="author-narrative" class=${classMap({ "is-pending": vm.authoring.value })} ?disabled=${vm.authoring.value} lang="de">
                ${vm.authoring.value ? vm.t("review.narrative.authoring") : vm.t("review.narrative.author")}
              </button>
            `}
      </section>
    `
  }

  protected render(vm: ProfileVm): TemplateResult {
    const p = vm.profile.value
    const err = vm.error.value
    if (!p) {
      return html`
        <section class="surface profile" urn=${vm.urn} aria-labelledby="profile-title">
          <h1 id="profile-title" class="surface-title" lang="de">${vm.t("surface.profil.title")}</h1>
          ${err ? html`<p class="surface-error" role="alert" lang="de">${vm.t("review.error", { message: err })}</p>` : nothing}
          ${vm.loaded.value ? html`<p class="surface-empty" lang="de">${vm.t("profile.empty")}</p>` : nothing}
        </section>
      `
    }
    return html`
      <section class="surface profile" urn=${vm.urn} aria-labelledby="profile-title">
        <h1 id="profile-title" class="surface-title" lang="de">${vm.t("surface.profil.title")}</h1>
        <p class="surface-lead" lang="de">${vm.t("profile.lead")}</p>
        ${err ? html`<p class="surface-error" role="alert" lang="de">${vm.t("review.error", { message: err })}</p>` : nothing}

        ${this.narrativePanel(vm)}

        ${this.identityPanel(vm, p)}

        <div class="profile-facet-grid">
          ${this.facetList(vm, "profile.facets.motivations", p.motivations)}
          ${this.facetList(vm, "profile.facets.drives", p.drives)}
          ${this.facetList(vm, "profile.facets.values", p.values)}
          ${this.facetList(vm, "profile.facets.antiValues", p.antiValues)}
          ${this.facetList(vm, "profile.facets.dealBreakers", p.dealBreakers)}
        </div>

        ${this.constraintsPanel(vm, p)}
        ${this.voicePanel(vm, p)}
      </section>
    `
  }

  private editButton(vm: ProfileVm, section: string): TemplateResult {
    return html`<button class="facet-edit" data-act="edit-facet" data-section=${section} lang="de">${vm.t("profile.edit")}</button>`
  }

  private saveCancel(vm: ProfileVm, section: string): TemplateResult {
    return html`
      <div class="facet-edit-actions">
        <button data-act="save-facet" data-section=${section} class=${classMap({ "is-pending": vm.saving.value })} ?disabled=${vm.saving.value} lang="de">${vm.t("profile.edit.save")}</button>
        <button data-act="cancel-facet" lang="de">${vm.t("profile.edit.cancel")}</button>
      </div>`
  }

  private identityPanel(vm: ProfileVm, p: ProfileView): TemplateResult {
    const editing = vm.editing.value === "identity"
    return html`
      <section class="profile-identity surface-panel" aria-labelledby="profile-identity-title">
        <h2 id="profile-identity-title" class="surface-section-title" lang="de">${vm.t("profile.identity.title")}</h2>
        ${editing
          ? html`<form class="facet-edit-form">
              <label><span lang="de">${vm.t("profile.identity.throughlineLabel")}</span>
                <input name="throughline" type="text" .value=${p.throughline} lang="de" /></label>
              <label><span lang="de">${vm.t("profile.identity.selfDescriptionLabel")}</span>
                <textarea name="selfDescription" rows="3" lang="de">${p.selfDescription}</textarea></label>
              ${this.saveCancel(vm, "identity")}
            </form>`
          : html`
              <p class="profile-throughline" lang="de">${p.throughline || vm.t("review.profile.noThroughline")}</p>
              ${p.selfDescription ? html`<p class="profile-selfdescription" lang="de">${p.selfDescription}</p>` : nothing}
              ${this.editButton(vm, "identity")}`}
      </section>`
  }

  private constraintsPanel(vm: ProfileVm, p: ProfileView): TemplateResult {
    const editing = vm.editing.value === "constraints"
    return html`
      <section class="profile-constraints surface-panel" aria-labelledby="profile-constraints-title">
        <h2 id="profile-constraints-title" class="surface-section-title" lang="de">${vm.t("profile.constraints.title")}</h2>
        ${editing
          ? html`<form class="facet-edit-form">
              <label><span lang="de">${vm.t("profile.constraints.location")}</span>
                <input name="location" type="text" .value=${p.constraints.location} lang="de" /></label>
              <label><span lang="de">${vm.t("profile.constraints.salary")}</span>
                <input name="salaryTarget" type="text" .value=${p.constraints.salaryTarget} lang="de" /></label>
              <fieldset class="workmode-set"><legend lang="de">${vm.t("profile.constraints.workModes")}</legend>
                ${WORK_MODES.map(
                  (m) => html`<label class="workmode-option"><input type="checkbox" name="workMode" value=${m} ?checked=${p.constraints.workModes.includes(m)} /><span lang="de">${vm.t(`profile.workmode.${m}` as CareerKey)}</span></label>`,
                )}
              </fieldset>
              ${this.saveCancel(vm, "constraints")}
            </form>`
          : html`
              <dl class="profile-facts">
                <dt lang="de">${vm.t("profile.constraints.location")}</dt><dd lang="de">${p.constraints.location || "—"}</dd>
                <dt lang="de">${vm.t("profile.constraints.salary")}</dt><dd lang="de">${p.constraints.salaryTarget || "—"}</dd>
                <dt lang="de">${vm.t("profile.constraints.workModes")}</dt><dd lang="de">${p.constraints.workModes.length > 0 ? p.constraints.workModes.join(", ") : "—"}</dd>
              </dl>
              ${this.editButton(vm, "constraints")}`}
      </section>`
  }

  private voicePanel(vm: ProfileVm, p: ProfileView): TemplateResult {
    const editing = vm.editing.value === "voice"
    return html`
      <section class="profile-voice surface-panel" aria-labelledby="profile-voice-title">
        <h2 id="profile-voice-title" class="surface-section-title" lang="de">${vm.t("profile.voice.title")}</h2>
        ${editing
          ? html`<form class="facet-edit-form">
              <label><span lang="de">${vm.t("profile.voice.samples")}</span>
                <textarea name="samples" rows="4" lang="de">${p.voiceSamples.join("\n")}</textarea></label>
              ${this.saveCancel(vm, "voice")}
            </form>`
          : html`
              ${p.voiceSamples.length > 0
                ? html`<ul class="voice-samples" role="list">${p.voiceSamples.map((s) => html`<li lang="de">${s}</li>`)}</ul>`
                : html`<p class="surface-empty" lang="de">${vm.t("profile.voice.empty")}</p>`}
              ${this.editButton(vm, "voice")}`}
      </section>`
  }

  protected override actions(vm: ProfileVm): ActionMap {
    const formOf = (event: Event): HTMLFormElement | null => (event.target as HTMLElement).closest("form")
    const val = (form: HTMLFormElement, name: string): string => form.querySelector<HTMLInputElement | HTMLTextAreaElement>(`[name="${name}"]`)?.value ?? ""
    return {
      "author-narrative": () => void vm.authorNarrative(),
      "edit-facet": ({ section }) => {
        if (section) vm.edit(section)
      },
      "cancel-facet": () => vm.cancelEdit(),
      "save-facet": ({ section }, event) => {
        const form = formOf(event)
        if (!form || !section) return
        if (section === "identity") void vm.saveIdentity(val(form, "throughline"), val(form, "selfDescription"))
        else if (section === "constraints") {
          const workModes = [...form.querySelectorAll<HTMLInputElement>('input[name="workMode"]:checked')].map((c) => c.value)
          void vm.saveConstraints(val(form, "location"), val(form, "salaryTarget"), workModes)
        } else if (section === "voice") {
          const samples = val(form, "samples").split("\n").map((s) => s.trim()).filter((s) => s.length > 0)
          void vm.saveVoice(samples)
        }
      },
      nav: ({ url }, event) => {
        event.preventDefault()
        if (url) vm.go(url)
      },
    }
  }
}

function toProfileView(p: any): ProfileView {
  return {
    throughline: String(p.identity?.throughline ?? ""),
    selfDescription: String(p.identity?.selfDescription ?? ""),
    motivations: facetsOf(p.motivations),
    drives: facetsOf(p.drives),
    values: facetsOf(p.values),
    antiValues: facetsOf(p.antiValues),
    dealBreakers: facetsOf(p.dealBreakers),
    trajectory: { now: String(p.trajectory?.now ?? ""), midTerm: String(p.trajectory?.midTerm ?? ""), longTerm: String(p.trajectory?.longTerm ?? "") },
    constraints: {
      location: String(p.constraints?.location ?? ""),
      salaryTarget: String(p.constraints?.salaryTarget ?? ""),
      workModes: Array.isArray(p.constraints?.workModes) ? p.constraints.workModes.map(String) : [],
    },
    hasVoice: Boolean(p.voiceProfile),
    voiceSamples: Array.isArray(p.voiceProfile?.samples) ? p.voiceProfile.samples.map(String) : [],
  }
}

function toNarrativeView(n: any, stale: boolean, authoredAt: string | null, degraded: boolean): NarrativeView {
  return {
    body: String(n.body ?? ""),
    provenanceCount: Array.isArray(n.provenance) ? n.provenance.length : 0,
    stale,
    authoredOn: authoredOnDate(authoredAt),
    degraded: degraded || Boolean(n.meta?.degraded),
  }
}
